import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  Text,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import { saudiRegions, regionNames } from '../data/saudiRegions';

const LocationManagement = () => {
  const { locations, users, addLocation, updateLocation, deleteLocation, canManageUsers, loadLocations, loadUsers, loadRegions, loadingLocations, locationsPagination } = useApp();
  const [currentPage, setCurrentPage] = useState(1);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteLocationId, setDeleteLocationId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Responsive breakpoints for pagination
  const showPageNumbers = useBreakpointValue({ base: false, sm: true });
  const showPageInfo = useBreakpointValue({ base: false, md: true });
  const buttonSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const pageButtonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    city: '',
    supervisorId: '',
  });

  // Load locations, users, and regions when component mounts
  useEffect(() => {
    loadLocations(currentPage);
    if (users.length === 0) {
      loadUsers();
    }
    loadRegions(); // Load regions for region selection dropdown
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load locations when page changes
  useEffect(() => {
    loadLocations(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  if (!canManageUsers()) {
    return (
      <Card>
        <Box p={6}>
          <Box textAlign="center" color="gray.500">
            ليس لديك صلاحية للوصول إلى هذه الصفحة.
          </Box>
        </Box>
      </Card>
    );
  }

  // Get cities for selected region
  const selectedRegionCities = formData.region ? (saudiRegions[formData.region] || []) : [];

  // Filter users to show only supervisors as potential supervisors
  // Only Region Supervisor and Location Supervisor can be assigned as supervisors
  const availableSupervisors = users.filter(user => 
    user.role === 'Region Supervisor' || user.role === 'Location Supervisor'
  );

  const handleOpenModal = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name || '',
        region: location.region || '',
        city: location.city || '',
        supervisorId: location.supervisorId || location.supervisor?.id || '',
      });
    } else {
      setEditingLocation(null);
      setFormData({
        name: '',
        region: '',
        city: '',
        supervisorId: '',
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    onClose();
    setEditingLocation(null);
    setFormData({
      name: '',
      region: '',
      city: '',
      supervisorId: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Prepare data with proper null handling
      const locationData = {
        name: formData.name,
        region: formData.region,
        city: formData.city,
        supervisorId: formData.supervisorId || null, // Convert empty string to null
      };
      
      if (editingLocation) {
        await updateLocation(editingLocation.id, locationData);
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث الموقع بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await addLocation(locationData);
        toast({
          title: 'تمت الإضافة',
          description: 'تم إضافة الموقع بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving location:', error);
      // Extract error message from Laravel validation errors
      let errorMessage = 'فشل في حفظ الموقع';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          // Laravel validation errors format
          const errors = error.response.data.errors;
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast({
        title: 'خطأ',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteLocationId(id);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteLocation(deleteLocationId);
      toast({
        title: 'تم الحذف',
        description: 'تم حذف الموقع بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setDeleteLocationId(null);
    } catch (error) {
      console.error('Error deleting location:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في حذف الموقع',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">إدارة المواقع</Heading>
        <Button colorScheme="primary" onClick={() => handleOpenModal()}>
          إضافة موقع جديد
        </Button>
      </Flex>

      <Card>
        {loadingLocations ? (
          <Box p={10}>
            <Center>
              <VStack spacing={4}>
                <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
                <Text color="gray.600">جاري تحميل المواقع...</Text>
              </VStack>
            </Center>
          </Box>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>اسم الموقع</Th>
                  <Th>المنطقة</Th>
                  <Th>المدينة</Th>
                  <Th>المشرف</Th>
                  <Th>الإجراءات</Th>
                </Tr>
              </Thead>
              <Tbody>
                {locations.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" color="gray.500">
                    لا توجد مواقع مسجلة
                  </Td>
                </Tr>
              ) : (
                locations.map((location) => (
                  <Tr key={location.id}>
                    <Td>{location.name}</Td>
                    <Td>{regionNames[location.region] || location.region}</Td>
                    <Td>{location.city}</Td>
                    <Td>{location.supervisor?.fullName || '-'}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenModal(location)}
                        >
                          تعديل
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleDeleteClick(location.id)}
                        >
                          حذف
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
        )}
      </Card>

      {/* Pagination */}
      {locationsPagination && locationsPagination.totalPages > 1 && (
        <Box mt={6}>
          <Flex 
            direction={{ base: 'column', md: 'row' }}
            justifyContent="center" 
            alignItems="center" 
            gap={{ base: 3, md: 4 }}
            flexWrap="wrap"
          >
            {/* Navigation Buttons */}
            <HStack spacing={2}>
              <IconButton
                aria-label="الصفحة السابقة"
                onClick={() => {
                  if (currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isDisabled={currentPage === 1 || loadingLocations}
                variant="outline"
                size={buttonSize}
                fontSize="xl"
                colorScheme="gray"
              >
                <Text as="span" color="currentColor" fontWeight="bold">←</Text>
              </IconButton>
              
              {/* Page Numbers - Hidden on small screens, shown on medium+ */}
              {showPageNumbers && (
                <HStack spacing={1}>
                  {(() => {
                    const totalPages = locationsPagination.totalPages || 1;
                    const current = currentPage;
                    const pages = [];
                    
                    // Show first page
                    if (current > 3) {
                      pages.push(1);
                      if (current > 4) {
                        pages.push('...');
                      }
                    }
                    
                    // Show pages around current
                    const start = Math.max(1, current - 2);
                    const end = Math.min(totalPages, current + 2);
                    
                    for (let i = start; i <= end; i++) {
                      pages.push(i);
                    }
                    
                    // Show last page
                    if (current < totalPages - 2) {
                      if (current < totalPages - 3) {
                        pages.push('...');
                      }
                      pages.push(totalPages);
                    }
                    
                    return pages.map((page, index) => {
                      if (page === '...') {
                        return (
                          <Text key={`ellipsis-${index}`} px={1} color="gray.500" fontSize="sm">
                            ...
                          </Text>
                        );
                      }
                      
                      return (
                        <Button
                          key={page}
                          size={pageButtonSize}
                          minW={{ base: '32px', md: '40px' }}
                          onClick={() => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          isDisabled={loadingLocations}
                          colorScheme={page === current ? 'primary' : 'gray'}
                          variant={page === current ? 'solid' : 'outline'}
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}
                </HStack>
              )}
              
              {/* Current page indicator for mobile */}
              <Text 
                display={{ base: 'block', sm: 'none' }} 
                fontSize="sm" 
                fontWeight="semibold"
                px={2}
              >
                {currentPage} / {locationsPagination.totalPages || 1}
              </Text>
              
              <IconButton
                aria-label="الصفحة التالية"
                onClick={() => {
                  const totalPages = locationsPagination.totalPages || 1;
                  if (currentPage < totalPages) {
                    setCurrentPage(currentPage + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                isDisabled={currentPage >= (locationsPagination.totalPages || 1) || loadingLocations}
                variant="outline"
                size={buttonSize}
                fontSize="xl"
                colorScheme="gray"
              >
                <Text as="span" color="currentColor" fontWeight="bold">→</Text>
              </IconButton>
            </HStack>
            
            {/* Page Info - Hidden on small screens */}
            {showPageInfo && (
              <Text 
                fontSize="sm" 
                color="gray.600"
                textAlign="center"
              >
                صفحة {currentPage} من {locationsPagination.totalPages || 1} ({locationsPagination.totalItems || 0} موقع)
              </Text>
            )}
          </Flex>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingLocation ? 'تعديل موقع' : 'إضافة موقع جديد'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>اسم الموقع</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    المنطقة
                  </FormLabel>
                  <Select
                    value={formData.region}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        region: e.target.value,
                        city: '' // Reset city when region changes
                      });
                    }}
                    size="lg"
                    focusBorderColor="primary.500"
                  >
                    <option value="">اختر المنطقة</option>
                    {regionNames.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    المدينة
                  </FormLabel>
                  <Select
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                    isDisabled={!formData.region}
                  >
                    <option value="">اختر المدينة</option>
                    {selectedRegionCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="semibold" mb={2} color="gray.700">
                    المشرف
                  </FormLabel>
                  <Select
                    value={formData.supervisorId}
                    onChange={(e) =>
                      setFormData({ ...formData, supervisorId: e.target.value })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                  >
                    <option value="">لا يوجد مشرف</option>
                    {availableSupervisors.length === 0 ? (
                      <option value="" disabled>لا يوجد مشرفين متاحين - يرجى إضافة مشرفين أولاً</option>
                    ) : (
                      availableSupervisors.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.fullName || user.full_name} ({user.username}) - {user.role === 'Region Supervisor' ? 'مشرف منطقة' : 'مشرف موقع'}
                        </option>
                      ))
                    )}
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <Box p={6} borderTop="1px solid" borderColor="gray.200">
              <HStack justify="flex-end" spacing={3}>
                <Button variant="outline" onClick={handleCloseModal}>
                  إلغاء
                </Button>
                <Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText={editingLocation ? 'جاري الحفظ...' : 'جاري الإضافة...'}>
                  {editingLocation ? 'حفظ التعديلات' : 'إضافة'}
                </Button>
              </HStack>
            </Box>
          </form>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} leastDestructiveRef={undefined}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              حذف الموقع
            </AlertDialogHeader>
            <AlertDialogBody>
              هل أنت متأكد من حذف هذا الموقع؟
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                إلغاء
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} mr={3} isLoading={isDeleting} loadingText="جاري الحذف...">
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default LocationManagement;

